import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Grid,
  Button,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import Dropzone from 'react-dropzone';
import CloudIcon from '@mui/icons-material/Cloud';

import ErrorMessage from '../common/error-message';
import {
  getPickupZonesFromKml,
  getHubsFromKml,
} from '../../lib/kmlParse-utils';

const styles = {
  dropzone: {
    width: '100%',
    borderRadius: '4px',
    border: '1px dashed #ccc',
  },
  list: {
    margin: 0,
    paddingLeft: '1.5em',
  },
};
const steps = {
  UPLOAD: 0,
  OVERWRITE: 1,
};
const texts = [
  {
    hubs: [
      'KML files must be exported in their entirety - not one layer at a time.',
      'Hubs must be in a map layer called \'Hub Locations\'.',
      'All newly created hubs will have a target and capacity of 5.',
      'Existing hubs with identical names will be overwritten with new location information and will be made active.',
      'Hubs with new unique names will be created.',
    ],
    zones: [
      'KML files must be exported in their entirety - not one layer at a time.',
      'Pick Up Zones must be in a map layer called \'Pick Up Zones\'.',
      'Pick Up Zone name cannot contain a period \'.\'',
      'Existing zones with identical names will be overwritten with new zone information.',
      'Zones with new unique names will be created.',
    ],
  },
  {
    hubs: [
      'Existing hubs whose names are not included in the KML file will be marked as inactive.',
    ],
    zones: [
      'Existing zones whose names are not included in the KML file will be marked as inactive.',
    ],
  },
];
const overwriteValues = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

function BulkUploadModal({ isOpen, onCancel, onSave, type }) {
  const [step, setStep] = useState(steps.UPLOAD);
  const [fileName, setFileName] = useState('');
  const [elements, setElements] = useState([]);
  const [error, setError] = useState(false);
  const [overwriteOld, setOverwriteOld] = useState(true);

  const nextStep = () => setStep(steps.OVERWRITE);

  const changeOverwrite = (event) => {
    const { value } = event.target;

    setOverwriteOld(value === 'true');
  };

  const closeModal = () => onCancel();

  const handleFileUpload = (files) => {
    if (!files.length) return;

    const file = files[0];

    setError(false);
    setFileName(file.name);

    const fileParts = file.name.split('.');
    const extension = fileParts.pop();
    const reader = new FileReader();

    reader.onload = async () => {
      if (extension !== 'kml') return;

      let uploadedData = null;

      if (type === 'hubs') {
        uploadedData = await getHubsFromKml(reader.result);
      } else {
        uploadedData = await getPickupZonesFromKml(reader.result);
      }

      setElements(uploadedData);

      if (!uploadedData || !uploadedData.length) {
        setError('Invalid KML file. Please upload a valid KML.');
      }
    };

    reader.readAsText(file);
  };

  const handleSave = () => {
    if (!elements || elements.length < 1) return;
    onSave(elements, overwriteOld);
  };

  return (
    <Dialog
      open={isOpen}
      maxWidth="sm"
      aria-labelledby="bulk-upload-form-dialog-title"
      onClose={closeModal}
    >
      <DialogTitle id="form-dialog-title">
        {step === steps.UPLOAD ? 'Upload KML File' : `Set existing ${type} as inactive?`}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {error && (
            <Grid item xs={12}>
              <ErrorMessage errorMessage={error} />
            </Grid>
          )}
          <Grid item sm={12}>
            {step === steps.OVERWRITE && (
              <Typography>
                {`The KML file has ${elements.length} ${type}.`}
              </Typography>
            )}
            <ul style={styles.list}>
              {texts[step][type].map((text) => (
                <li key={text}>
                  <Typography>{text}</Typography>
                </li>
              ))}
            </ul>
          </Grid>
          <Grid item sm={12}>
            {step === steps.UPLOAD && (
              <Dropzone accept={{ 'application/vnd': ['.kml'] }} onDrop={handleFileUpload}>
                {({ getRootProps, getInputProps }) => (
                  <Grid
                    {...getRootProps({ className: 'dropzone' })}
                    container
                    alignItems="center"
                    justifyContent="center"
                    direction="column"
                    style={styles.dropzone}
                    sx={{ px: 3, py: 6 }}
                  >
                    <input {...getInputProps()} accept=".kml" />
                    <Typography sx={{ color: 'grey.700' }}>
                      {fileName
                        ? `${fileName} — ${elements?.length || 0} ${type} detected`
                        : 'Drag and drop a KML file here, or click to select file'}
                    </Typography>
                    <CloudIcon sx={{ mt: 1 }} />
                  </Grid>
                )}
              </Dropzone>
            )}
            {step === steps.OVERWRITE && (
              <RadioGroup
                row
                value={overwriteOld}
                name="overwriteOldElements"
                onChange={changeOverwrite}
              >
                {overwriteValues.map((option) => (
                  <FormControlLabel
                    key={option.label}
                    value={option.value}
                    control={<Radio />}
                    label={option.label}
                  />
                ))}
              </RadioGroup>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={closeModal} variant="outlined">Cancel</Button>
        {step === steps.UPLOAD && (
          <Button
            onClick={nextStep}
            variant="contained"
            disabled={!elements || !elements.length}
          >
            Next
          </Button>
        )}
        {step === steps.OVERWRITE && (
          <Button onClick={handleSave} variant="contained">Save</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default BulkUploadModal;
